<template>
  <div class="blog">
    <div class="container">
      <div class="row">
        <div class="col-lg-8">
          <h2>{{ title }}</h2>
          <div>
             <img src="@/assets/images/assets/blog/avoid_surprise_aws_bills/simple_guide_avoid_aws_bills.svg" alt="RAG System Guide" />
          </div>
          <div v-html="sanitizedContent"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';
import billingAlarm from '@/assets/images/assets/blog/avoid_surprise_aws_bills/billing_alarm.png';
import budgetAlarm from '@/assets/images/assets/blog/avoid_surprise_aws_bills/budget_alarm.png';
import cloudWatchAlarms from '@/assets/images/assets/blog/avoid_surprise_aws_bills/cloud_watch_alarms.png';
import throttling from '@/assets/images/assets/blog/avoid_surprise_aws_bills/throttling.png';

export default {
  name: 'BlogPostRagSystemGuide',
  data() {
    return {
      title: 'Simple Guide on How to Avoid Surprise AWS Bills',
      content: `

<h3>Simple Guide on How to Avoid Surprise AWS Bills</h3>

<p>Without much introduction, here’s a simple guide on how to do it (with links to official documentation).</p>

<h3>1. Billing Alarm</h3>
         <div class="documentation-image">
          <img
            src="${billingAlarm}"
            alt="Billing Alarm"
            style="width: 70%; height: 70%; display: block; margin: 30px auto;"
          />
        </div>
<p>When your spend for the month exceeds your threshold, you get a notification.</p>
<p>This is the <b>number one thing</b> you should do after creating an AWS account.</p>
<p>My advice is to set up <b>several alarms</b> (e.g., with thresholds of $10 and $20).</p>
<p>The downside is that this only notifies you about your total spend. For more granularity, you can set up a <b>budget</b>.</p>
<p><a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/monitor_estimated_charges_with_cloudwatch.html" target="_blank">Official AWS Guide on setting up billing alarms</a></p>

<h3>2. Budget</h3>
         <div class="documentation-image">
          <img
            src="${budgetAlarm}"
            alt="Budget Alarm"
            style="width: 70%; height: 70%; display: block; margin: 30px auto;"
          />
        </div>
<p>This is similar to billing alarms, but with <b>advanced options</b>. Billing alarms notify you of your <b>total spend</b>, while <b>budgets</b> let you filter by Region, Service Type, Instance Type, AZ, Tag, or Linked Account.</p>
<p>You can also configure <b>actions</b>, like automatically stopping EC2 (virtual machines) or RDS (databases) when a threshold is reached.</p>
<p>Another option is to <b>trigger a Lambda</b> using an SNS topic when the budget threshold is exceeded. This way, your own code can shut down resources.</p>
<p><a href="https://docs.aws.amazon.com/cost-management/latest/userguide/budgets-managing-costs.html" target="_blank">Official AWS Budget docs with tutorials</a></p>

<h3>3. CloudWatch Alarms</h3>
         <div class="documentation-image">
          <img
            src="${cloudWatchAlarms}"
            alt="CloudWatch Alarm"
            style="width: 70%; height: 70%; display: block; margin: 30px auto;"
          />
        </div>
<p><b>CloudWatch alarms</b> are tied to specific <b>metrics</b> of AWS services.</p>
<p>I call them <b>“traffic” alarms</b> — they monitor how much traffic your services are getting.</p>
<p>For example, I get a notification if <b>API Gateway requests exceed 1,000 per minute</b>. That’s either an attack or something suspicious.</p>
<p><a href="https://docs.aws.amazon.com/apigateway/latest/developerguide/monitoring-cloudwatch.html" target="_blank">Monitoring API Gateway with CloudWatch</a></p>

<h3>4. Throttling</h3>
         <div class="documentation-image">
          <img
            src="${throttling}"
            alt="Throttling"
            style="width: 70%; height: 70%; display: block; margin: 30px auto;"
          />
        </div>
<p><b>Throttling</b> (or rate limiting) lets you limit the number of requests to your endpoint — either by the <b>burst limit</b> or <b>rate limit</b>.</p>
<p>AWS uses the <b>token bucket algorithm</b>, a standard approach for rate limiting.</p>
<p>If you’re using <b>API Gateway</b>, follow this guide to set it up:</p>
<p><a href="https://docs.aws.amazon.com/apigateway/latest/developerguide/api-gateway-request-throttling.html" target="_blank">Throttling docs for API Gateway</a></p>

<h3>5. CDN</h3>
<p>A <b>CDN (Content Delivery Network)</b> caches your website (usually the frontend) globally. When users revisit the site, it’s served from the <b>local cache</b>, not your servers.</p>
<p>In AWS, the CDN is <b>CloudFront</b>. You can use it directly or through <b>AWS Amplify Hosting</b>.</p>
<p><a href="https://docs.aws.amazon.com/Route53/latest/DeveloperGuide/getting-started-cloudfront-overview.html" target="_blank">Hosting with S3 + CloudFront</a><br>
<a href="https://docs.aws.amazon.com/amplify/latest/userguide/getting-started.html" target="_blank">Using Amplify Hosting</a></p>

<h3>6. Caching</h3>
<p>Caching avoids hitting your backend resourcers (e.g. database) unnecessarily. For example, caching read requests to DynamoDB saves money and time.</p>
<p><a href="https://aws.amazon.com/dynamodbaccelerator/" target="_blank">DAX for DynamoDB</a></p>

<h3>7. Automatic Downscaling of Cloud Resources</h3>
<p>If you're running compute instances 24/7, you can save a lot by <b>downscaling at night</b> (based on time zones).</p>
<p><a href="https://docs.aws.amazon.com/AmazonECS/latest/developerguide/service-auto-scaling.html" target="_blank">ECS Auto Scaling Guide</a></p>
<p><b>Serverless services</b> like Lambda, DynamoDB (when on-demand), and API Gateway usually scale to zero automatically when not in use.</p>

<h3>8. Password Protection for Dev Environments</h3>
<p>It’s a small thing, but <b>password-protecting</b> your dev or pre-launch site is a good idea.</p>
<p>If you use <b>AWS Amplify Hosting</b>, you can enable this easily:</p>
<p><a href="https://docs.aws.amazon.com/amplify/latest/userguide/access-control.html" target="_blank">Access Control in Amplify Hosting</a></p>

<h3>9. AWS WAF</h3>
<p><b>AWS WAF</b> filters incoming HTTP(S) traffic before it reaches your app.</p>
<p>You can attach it to <b>CloudFront</b>, <b>API Gateway</b>, <b>ALB</b>, <b>AppSync</b>, or even <b>Amplify Hosting</b>.</p>
<p><a href="https://docs.aws.amazon.com/waf/" target="_blank">AWS WAF Docs</a></p>

<h3>10. CORS Policy</h3>
<p><b>CORS (Cross-Origin Resource Sharing)</b> is a browser feature that controls which websites can communicate with your backend.</p>
<p>By default, browsers block cross-origin requests. You can allow specific domains using headers like:</p>
<pre>Access-Control-Allow-Origin: https://myfrontend.com</pre>
<pre>Access-Control-Allow-Methods: GET, POST</pre>
<p><a href="https://docs.aws.amazon.com/apigateway/latest/developerguide/how-to-cors.html" target="_blank">CORS in API Gateway</a><br>
<a href="https://docs.powertools.aws.dev/lambda/python/latest/core/event_handler/api_gateway/#cors" target="_blank">CORS with AWS Lambda Powertools</a></p>
<p><b>Note:</b> CORS only protects browser users. It does not block curl or Postman. But it can reduce unnecessary browser-based traffic.</p>

<h3>Out-of-the-Box Solution</h3>
<p>If you want a setup with many of these features <b>already included</b>, check out the <b>SaaS Template for AWS Cloud</b> I wrote: <a href="https://saasconstruct.com" target="_blank">saasconstruct.com</a></p>

<p>Besides payments, authentication, and user management, it comes with:</p>
<ul>
  <li><b>Traffic alarms</b></li>
  <li><b>Throttling</b> in API Gateway</li>
  <li><b>CDN</b> via Amplify Hosting</li>
  <li><b>Basic caching</b> layer in Lambda</li>
  <li><b>Serverless resources</b> that scale to zero</li>
  <li><b>Password protection</b> for dev environments</li>
  <li><b>CORS policy</b></li>
</ul>

<p><b>My monthly AWS cost with this setup?</b> Around <b>$1–3</b>.</p>
<p>Follow the <a href="https://saasconstruct.com/documentation/get-started" target="_blank">docs</a> to set it up and avoid surprises.


      `,
    };
  },
  computed: {
    sanitizedContent() {
      const config = { ADD_ATTR: ['target'] };
      return DOMPurify.sanitize(this.content, config);
    }
  }
};
</script>
